import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft,
  FiChevronDown,
  FiEye,
  FiUser,
} from 'react-icons/fi';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/Toast';
import Spinner from '../../components/ui/Spinner';
import SelectLeadModal from '../../components/Calendar/SelectLeadModal';
import ApplicationsSectionTabs from './ApplicationsSectionTabs';

const STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-amber-500' },
  { value: 'submitted', label: 'Submitted', color: 'bg-blue-500' },
  { value: 'reviewed', label: 'Reviewed', color: 'bg-green-500' },
  { value: 'archived', label: 'Archived', color: 'bg-gray-400' },
];

/** Statuses managers may set from the CRM detail page (not draft). */
const EDITABLE_STATUSES = STATUSES.filter((s) => s.value !== 'draft');

const PLAIN_SECTIONS = [
  {
    title: 'Disclosure & Privacy',
    fields: [
      ['Communication Consent', 'consent_communication'],
      ['Statement Confirmed', 'consent_truthful_information'],
    ],
  },
  {
    title: 'Personal Information',
    fields: [
      ['Full Name', 'full_name'],
      ['Birth State/Country', 'birth_state_country'],
      ['Birth Date', 'birth_date'],
      ['Gender', 'gender'],
      ['Address', 'address'],
      ['Years At Address', 'years_at_address'],
      ['Cell Phone', 'cell_phone'],
      ['Email', 'email'],
      ['Citizenship', 'citizenship'],
      ['ID Type', 'id_type'],
      ['ID Number', 'id_number'],
      ['ID State', 'id_state'],
      ['ID Expiration', 'id_expiration'],
    ],
    masked: [['SSN/ITIN', 'ssn']],
  },
  {
    title: 'Employment',
    fields: [
      ['Employer Name', 'employer_name'],
      ['Employer Address', 'employer_address'],
      ['Occupation', 'occupation'],
      ['Duties', 'duties'],
      ['Employer Phone', 'employer_phone'],
      ['Years At Employer', 'years_at_employer'],
    ],
  },
  {
    title: 'Financial Information',
    fields: [
      ['Annual Income', 'annual_income'],
      ['Household Income', 'household_income'],
      ['Monthly Expenses', 'monthly_expenses'],
      ['Net Worth', 'net_worth'],
    ],
  },
  {
    title: 'Insurance Information',
    fields: [
      ['Insurance Type', 'insurance_type'],
      ['Monthly Premium', 'monthly_premium'],
      ['Insurance Option', 'insurance_option'],
      ['Indexed Strategy 1', 'indexed_strategy_1'],
      ['Indexed Strategy 2', 'indexed_strategy_2'],
      ['Existing Policies', 'existing_policies'],
    ],
  },
  {
    title: 'Bank Information',
    fields: [['Bank Name', 'bank_name']],
    masked: [
      ['Routing Number', 'routing_number'],
      ['Account Number', 'account_number'],
    ],
  },
  {
    title: 'Illustration / Quote',
    fields: [['Rate Class', 'rate_class']],
  },
  {
    title: 'Health Questionnaire (Primary Insured)',
    fields: [
      ['Health Provider', 'health_provider'],
      ['Last Medical Visit', 'last_medical_visit'],
      ['Height', 'height'],
      ['Weight', 'weight'],
      ['Weight Change 10lbs', 'weight_change_10lbs'],
      ['Is Smoker', 'is_smoker'],
      ['Medications 5yr', 'medications_5yr'],
      ['Other Specialists 5yr', 'other_specialists_5yr'],
      ['Other Specialists Detail', 'other_specialists_detail'],
      ['Father Details', 'father_details'],
      ['Mother Details', 'mother_details'],
      ['Siblings Details', 'siblings_details'],
      ['International Travel', 'international_travel'],
      ['Dangerous Activities', 'dangerous_activities'],
      ['Unpaid Tickets', 'unpaid_tickets'],
      ['Exercises Regularly', 'exercises_regularly'],
      ['Health Conditions', 'health_conditions'],
    ],
  },
  {
    title: 'Agent Comments',
    fields: [
      ['Agent Name/Code', 'agent_name_code'],
      ['Split Agent Name/Code', 'split_agent_name_code'],
      ['Agent Carrier', 'agent_carrier'],
      ['Agent Indexed Strategies', 'agent_indexed_strategies'],
      ['Agent Other Comments', 'agent_other_comments'],
    ],
  },
];

const SPOUSE_SECTION = {
  title: 'Spouse / Secondary Insured',
  fields: [
    ['Spouse Application Type', 'spouse_application_type'],
    ['Spouse Full Name', 'spouse_full_name'],
    ['Spouse Birth Date', 'spouse_birth_date'],
    ['Spouse Gender', 'spouse_gender'],
    ['Spouse Citizenship', 'spouse_citizenship'],
    ['Spouse ID Type', 'spouse_id_type'],
    ['Spouse ID Number', 'spouse_id_number'],
    ['Spouse ID State', 'spouse_id_state'],
    ['Spouse ID Expiration', 'spouse_id_expiration'],
    ['Spouse Coverage Amount', 'spouse_coverage_amount'],
    ['Spouse Coverage Type', 'spouse_coverage_type'],
    ['Spouse Health Provider', 'spouse_health_provider'],
    ['Spouse Last Medical Visit', 'spouse_last_medical_visit'],
    ['Spouse Height', 'spouse_height'],
    ['Spouse Weight', 'spouse_weight'],
    ['Spouse Weight Change 10lbs', 'spouse_weight_change_10lbs'],
    ['Spouse Is Smoker', 'spouse_is_smoker'],
    ['Spouse Medications 5yr', 'spouse_medications_5yr'],
    ['Spouse Other Specialists', 'spouse_other_specialists_5yr'],
    ['Spouse Other Specialists Detail', 'spouse_other_specialists_detail'],
    ['Spouse International Travel', 'spouse_international_travel'],
    ['Spouse Dangerous Activities', 'spouse_dangerous_activities'],
    ['Spouse Unpaid Tickets', 'spouse_unpaid_tickets'],
    ['Spouse Exercises Regularly', 'spouse_exercises_regularly'],
    ['Spouse Health Conditions', 'spouse_health_conditions'],
  ],
  masked: [['Spouse SSN', 'spouse_ssn']],
};

function formatValue(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value === 'standalone') return 'Standalone Policy';
  if (value === 'rider') return 'Rider';
  return String(value);
}

function formatDate(val) {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return String(val);
  }
}

function StatusBadge({ status }) {
  const s = STATUSES.find((x) => x.value === status);
  if (!s) return null;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${s.color}`}>
      {s.label}
    </span>
  );
}

function StatusDropdown({ currentStatus, onUpdate, disabled }) {
  const [open, setOpen] = useState(false);
  // Drafts are created by the public form only — CRM can label them but not set status to draft.
  if (currentStatus === 'draft') {
    return <StatusBadge status={currentStatus} />;
  }
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        className="flex items-center gap-1 group"
      >
        <StatusBadge status={currentStatus} />
        <FiChevronDown className="w-3 h-3 text-gray-400 group-hover:text-white transition-colors" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-48 bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden">
          {EDITABLE_STATUSES.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => {
                onUpdate(s.value);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-700 transition-colors flex items-center gap-2 ${
                s.value === currentStatus ? 'bg-gray-700' : ''
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${s.color}`} />
              <span className="text-gray-200">{s.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FieldCell({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <div className="mt-1 text-sm text-gray-100 whitespace-pre-wrap break-words">
        {children}
      </div>
    </div>
  );
}

function isAuthError(err) {
  const msg = String(err?.message || '').toLowerCase();
  return (
    msg.includes('forbidden')
    || msg.includes('not authorized')
    || msg.includes('permission')
    || msg.includes('unauthorized')
    || msg.includes('403')
  );
}

function MaskedRevealField({
  label,
  fieldKey,
  kind = 'scalar',
  revealed,
  revealing,
  error,
  onReveal,
}) {
  const value = revealed[fieldKey];
  const loading = Boolean(revealing[fieldKey]);
  const err = error[fieldKey];

  let maskedLabel = '•••••••• (hidden)';
  if (kind === 'array') {
    maskedLabel = 'Hidden — click Reveal to view';
  }

  return (
    <FieldCell label={label}>
      {value !== undefined ? (
        kind === 'array' ? (
          <ArrayRevealList fieldKey={fieldKey} value={value} />
        ) : (
          <span className="font-mono tracking-wide">{formatValue(value)}</span>
        )
      ) : (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="text-gray-400">{maskedLabel}</span>
            <button
              type="button"
              onClick={() => onReveal(fieldKey)}
              disabled={loading}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs
                         text-[#00AAFF] hover:bg-gray-800 border border-gray-700
                         disabled:opacity-50 transition-colors"
            >
              {loading ? <Spinner className="w-3 h-3 text-[#00AAFF]" /> : <FiEye className="w-3 h-3" />}
              Reveal
            </button>
          </div>
          {err && (
            <p className="text-xs text-amber-400">{err}</p>
          )}
        </div>
      )}
    </FieldCell>
  );
}

function ArrayRevealList({ fieldKey, value }) {
  const rows = Array.isArray(value) ? value : [];
  if (!rows.length) {
    return <span className="text-gray-400">No records</span>;
  }

  if (fieldKey === 'beneficiaries') {
    return (
      <ul className="space-y-2">
        {rows.map((entry, idx) => (
          <li key={idx} className="rounded-lg border border-gray-700 bg-gray-800/60 p-3 text-xs space-y-1">
            <p className="font-medium text-gray-100">#{idx + 1} {entry.full_name || '—'}</p>
            <p className="text-gray-400">DOB: {formatValue(entry.dob)}</p>
            <p className="text-gray-400">Relationship: {formatValue(entry.relationship)}</p>
            <p className="text-gray-400">SSN: {formatValue(entry.ssn)}</p>
            <p className="text-gray-400">Percentage: {formatValue(entry.percentage)}</p>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ul className="space-y-2">
      {rows.map((entry, idx) => (
        <li key={idx} className="rounded-lg border border-gray-700 bg-gray-800/60 p-3 text-xs space-y-1">
          <p className="font-medium text-gray-100">
            #{idx + 1} {entry.full_name || '—'}
            {entry.application_type ? ` (${formatValue(entry.application_type)})` : ''}
          </p>
          <p className="text-gray-400">DOB: {formatValue(entry.dob)}</p>
          <p className="text-gray-400">SSN: {formatValue(entry.ssn)}</p>
          <p className="text-gray-400">Height: {formatValue(entry.height)}</p>
          <p className="text-gray-400">Weight: {formatValue(entry.weight)}</p>
          <p className="text-gray-400">Conditions: {formatValue(entry.known_conditions || entry.conditions)}</p>
          <p className="text-gray-400">Medications: {formatValue(entry.medications)}</p>
        </li>
      ))}
    </ul>
  );
}

function SectionCard({ title, children }) {
  return (
    <section className="rounded-xl border border-gray-700 bg-[#1a2736] overflow-hidden">
      <div className="bg-[#1B4C87] px-4 py-2.5 text-sm font-semibold text-white">
        {title}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 p-4">
        {children}
      </div>
    </section>
  );
}

function renderPlainFields(log, fields) {
  return fields.map(([label, key]) => (
    <FieldCell key={key} label={label}>
      {key.includes('date') || key.endsWith('_expiration') || key === 'birth_date' || key === 'spouse_birth_date'
        ? formatDate(log[key])
        : formatValue(log[key])}
    </FieldCell>
  ));
}

export default function PreUnderwritingLogDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();

  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [linking, setLinking] = useState(false);

  // In-memory only — cleared when leaving the page
  const [revealed, setRevealed] = useState({});
  const [revealing, setRevealing] = useState({});
  const [revealErrors, setRevealErrors] = useState({});

  const isManagerPlus = ['admin', 'super_admin', 'manager'].includes(user?.role);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.preUnderwritingLogs.getById(id);
      setLog(data);
      setRevealed({});
      setRevealing({});
      setRevealErrors({});
    } catch {
      addToast({ type: 'error', message: 'Failed to load log' });
      setLog(null);
    } finally {
      setLoading(false);
    }
  }, [id, addToast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleStatusUpdate = async (status) => {
    try {
      setUpdatingStatus(true);
      const updated = await api.preUnderwritingLogs.updateStatus(id, status);
      setLog((prev) => ({ ...prev, ...updated }));
      addToast({ type: 'success', message: 'Status updated' });
    } catch {
      addToast({ type: 'error', message: 'Failed to update status' });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleReveal = async (field) => {
    setRevealErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setRevealing((prev) => ({ ...prev, [field]: true }));
    try {
      const result = await api.preUnderwritingLogs.revealField(id, field);
      setRevealed((prev) => ({ ...prev, [field]: result.value }));
    } catch (err) {
      if (isAuthError(err)) {
        setRevealErrors((prev) => ({
          ...prev,
          [field]: "You don't have permission to view this field",
        }));
      } else {
        setRevealErrors((prev) => ({
          ...prev,
          [field]: err?.message || 'Failed to reveal field',
        }));
      }
    } finally {
      setRevealing((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleLinkLead = async (lead) => {
    try {
      setLinking(true);
      const updated = await api.preUnderwritingLogs.linkLead(id, lead.id);
      setLog((prev) => ({
        ...prev,
        ...updated,
        lead: {
          id: lead.id,
          first_name: lead.first_name,
          last_name: lead.last_name,
        },
      }));
      addToast({ type: 'success', message: 'Lead linked' });
    } catch {
      addToast({ type: 'error', message: 'Failed to link lead' });
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkLead = async () => {
    try {
      setLinking(true);
      const updated = await api.preUnderwritingLogs.linkLead(id, null);
      setLog((prev) => ({ ...prev, ...updated, lead: null, lead_id: null }));
      addToast({ type: 'success', message: 'Lead unlinked' });
    } catch {
      addToast({ type: 'error', message: 'Failed to unlink lead' });
    } finally {
      setLinking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 gap-3">
        <Spinner size="lg" className="text-[#00AAFF]" />
        <p className="text-gray-400 text-sm">Loading log…</p>
      </div>
    );
  }

  if (!log) {
    return (
      <div className="p-6 text-center space-y-4">
        <p className="text-gray-400 text-sm">Log not found.</p>
        <button
          type="button"
          onClick={() => navigate('/applications/pre-underwriting')}
          className="text-sm text-[#00AAFF] hover:underline"
        >
          Back to Pre-Underwriting Logs
        </button>
      </div>
    );
  }

  const leadLabel = log.lead
    ? [log.lead.first_name, log.lead.last_name].filter(Boolean).join(' ').trim() || 'Linked lead'
    : null;

  const sections = [...PLAIN_SECTIONS];
  if (log.has_spouse) {
    const agentIdx = sections.findIndex((s) => s.title === 'Agent Comments');
    sections.splice(agentIdx, 0, {
      ...SPOUSE_SECTION,
      title: `Spouse / Secondary Insured${
        log.spouse_application_type === 'standalone'
          ? ' (Standalone Policy)'
          : log.spouse_application_type === 'rider'
            ? ' (Rider)'
            : ''
      }`,
    });
  }

  return (
    <div className="p-6 space-y-6 pb-16">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => navigate('/applications/pre-underwriting')}
            className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors"
          >
            <FiArrowLeft className="w-3.5 h-3.5" />
            Back to logs
          </button>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center text-gray-500 shrink-0">
              <FiUser className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{log.full_name || 'Untitled'}</h1>
              <p className="text-sm text-gray-400 mt-1">
                Submitted {formatDate(log.created_at)}
                {log.agent?.full_name ? ` · Agent: ${log.agent.full_name}` : ''}
              </p>
              <div className="mt-3 flex items-center gap-3">
                {isManagerPlus ? (
                  <StatusDropdown
                    currentStatus={log.status}
                    onUpdate={handleStatusUpdate}
                    disabled={updatingStatus}
                  />
                ) : (
                  <StatusBadge status={log.status} />
                )}
              </div>
            </div>
          </div>
        </div>
        <ApplicationsSectionTabs />
      </div>

      <section className="rounded-xl border border-gray-700 bg-[#1a2736] p-4">
        <div className="flex items-center gap-2 mb-3">
          <FiUser className="w-4 h-4 text-[#00AAFF]" />
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">
            Linked Lead
          </h3>
        </div>
        {log.lead_id && leadLabel ? (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-700 bg-gray-800 p-3">
            <div>
              <p className="text-sm font-medium text-white">{leadLabel}</p>
              <Link
                to={`/leads/${log.lead_id}`}
                className="text-xs text-[#00AAFF] hover:underline"
              >
                View lead
              </Link>
            </div>
            <button
              type="button"
              onClick={handleUnlinkLead}
              disabled={linking}
              className="text-xs text-gray-500 hover:text-red-400 transition-colors disabled:opacity-50"
            >
              Unlink
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setLeadModalOpen(true)}
            disabled={linking}
            className="text-xs text-gray-400 hover:text-white transition-colors"
          >
            Link to Lead
          </button>
        )}
      </section>

      {sections.map((section) => (
        <SectionCard key={section.title} title={section.title}>
          {renderPlainFields(log, section.fields || [])}
          {(section.masked || []).map(([label, fieldKey]) => (
            <MaskedRevealField
              key={fieldKey}
              label={label}
              fieldKey={fieldKey}
              revealed={revealed}
              revealing={revealing}
              error={revealErrors}
              onReveal={handleReveal}
            />
          ))}
        </SectionCard>
      ))}

      {log.has_children && (
        <SectionCard title="Children">
          <div className="sm:col-span-2">
            <MaskedRevealField
              label="Children records"
              fieldKey="children"
              kind="array"
              revealed={revealed}
              revealing={revealing}
              error={revealErrors}
              onReveal={handleReveal}
            />
          </div>
        </SectionCard>
      )}

      <SectionCard title="Beneficiaries">
        <div className="sm:col-span-2">
          <MaskedRevealField
            label="Beneficiaries"
            fieldKey="beneficiaries"
            kind="array"
            revealed={revealed}
            revealing={revealing}
            error={revealErrors}
            onReveal={handleReveal}
          />
        </div>
      </SectionCard>

      <SelectLeadModal
        isOpen={leadModalOpen}
        onClose={() => setLeadModalOpen(false)}
        agencyId={user?.agency_id}
        onSelect={handleLinkLead}
      />
    </div>
  );
}
